<template>
    <accordion-item show-open>
        <h2 slot="header">
            All Heads of Loss
        </h2>
        <h2 class="title">
            <span v-if="chosenRates.length === 2">
                Comparison of
            </span>
            {{ tableType === 'average' ? 'Average' : 'Total' }} Heads of Loss
        </h2>
        <div>
            <div class="p-discount-rate-comparison__chart-select">
                <option-filters name="tableType">
                    <li :class="{'m-option-filters--active' : tableType === 'average'}" @click="updateTable('average')">Average</li>
                    <li :class="{'m-option-filters--active' : tableType === 'total'}" @click="updateTable('total')">Total</li>
                </option-filters>
            </div>
        </div>
        <div class="flex-grid u-margin-bottom-20">
            <div class="u-d-flex u-flex-center u-full-width p-discount-rate-comparison__pie-container">
                <pie v-for="rate in chosenRates" title="" :rate="rate" :labels="labels" :dataset="dataset(rate)" :legend="false"></pie>
            </div>
        </div>
        <div class="u-text-center">
            <chart-legend 
                :values="chartLegend()"
                :legendWidth="chartLegendWidth"
                marginTop="30"
                marginBottom="30"
            >
            </chart-legend>
        </div>
        <div class="a-table--short-no-style">
            <table class="a-table u-full-width">
                <tr>
                    <th rowspan="2">
                        <h2>
                            Heads of Loss
                        </h2>
                    </th>
                    <th :colspan="numberOfRates">
                        <h2>
                            Amount Calculated by Discount Rate
                        </h2>
                    </th>
                    <th colspan="2" v-if="chosenRates.length === 2">
                        <h2>
                            Difference
                        </h2>
                    </th>
                </tr>
                <tr>
                    <th v-for="rate in chosenRates">
                        <h2>
                            {{ rate }}
                        </h2>
                    </th>
                    <th v-if="chosenRates.length === 2">
                        <h2>
                            &pound;
                        </h2>
                    </th>
                    <th v-if="chosenRates.length === 2">
                        <h2>
                            %
                        </h2>
                    </th>
                </tr>
                <head-of-loss-row v-if="headOfLoss != undefined" v-for="(headOfLoss, name) in table()" :name="name" :row="headOfLoss"></head-of-loss-row>
                <tfoot>
                    <tr>
                        <td>
                            Total
                        </td>
                        <td v-for="rate in chosenRates">
                            {{ currencyFormat(sumOf(rate)) }}
                        </td>
                        <td v-if="chosenRates.length === 2">
                            {{ currencyFormat(sumOf(chosenRates[0]) - sumOf(chosenRates[1])) }}
                            
                        </td>
                        <td v-if="chosenRates.length === 2">
                            {{ roundAndPercent(sumOf(chosenRates[0]), sumOf(chosenRates[1])) }}%
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </accordion-item>
</template>

<script>
    import _ from 'lodash';
    import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
    import AccordionItem from 'components/accordion/AccordionItem.vue';
    import currencyFormat from 'mixins/currencyFormat';
    import percentageDifference from 'mixins/percentageDifference';
    import roundValue from 'mixins/roundValue';
    import Pie from 'pages/discount-rate-comparison/components/charts/Pie.vue';
    import HeadOfLossRow from 'pages/discount-rate-comparison/components/tables/HeadOfLossRow.vue';
    import OptionFilters from 'components/OptionFilters.vue';
    import ChartLegend from 'components/chart/Legend.vue';

    export default {
        name: 'all-heads-of-loss',
        components: {
            AccordionItem, 
            HeadOfLossRow,
            Pie,
            OptionFilters,
            ChartLegend
        },
        mixins: [
            currencyFormat,
            roundValue,
            percentageDifference,
        ],
        data() {
            return {
                tableType: "average",
            }
        },
        computed: {
            ...mapState('discountRateComparison', [
                'chosenRates',
                'claimType'
            ]),
            ...mapGetters('discountRateComparison', [
                'averageHeadsOfLossGraph',
                'averageHeadsOfLossTable',
                'totalHeadsOfLossGraph',
                'totalHeadsOfLossTable',
            ]),
            ...mapGetters('windowWidth', ['theCurrentWidth']),
            chartLegendWidth() {
                switch (true) {
                    case this.theCurrentWidth <= 700:
                        return 100;
                        break;
                    case this.theCurrentWidth <= 900:
                        return 90;
                        break;
                    case this.theCurrentWidth <= 1024:
                        return 70;
                        break;
                    default:
                        return 80;
                }
            },
            colours() {
                return [
                    '#45BFFC',
                    '#3DF2C3',
                    '#CD5CD8',
                    '#EB1458',
                    '#7DD3F4',
                    '#94F7DB',
                    '#E18FEF',
                    '#F94B8A',
                    '#4A89E5',
                    '#EA7A68',
                    '#9A4CD9',
                    '#BF104F',
                    '#422960',
                    '#007180',
                    '#2ECE9D'
                ];
            },
            headOfLossOrder() {
                let fatalAccidentDependency = [
                    'generalDamages',
                    'bereavementAward',
                    'funeralExpenses',
                    'lossOfSpouse',
                    'pastLossOfEarnings',
                    'pastCostOfTravel',
                    'miscellaneous',
                    'pastCostOfCare',
                    'customHeadsOfLoss',
                    'pastDependencyOnIncome',
                    'futureDependencyOnIncome',
                    'pastDependencyOnServices',
                    'futureDependencyOnServices'
                ];

                let fatalAccidentEstate = [
                    'generalDamages',
                    'funeralExpenses',
                    'lossOfSpouse',
                    'pastLossOfEarnings',
                    'pastCostOfTravel',
                    'miscellaneous',
                    'pastCostOfCare',
                    'customHeadsOfLoss',
                ];

                let lostYears = [
                    'generalDamages',
                    'futureLossTotal',
                    'miscellaneous',
                    'pastCostOfCare',
                    'pastLossOfEarnings',
                    'pastCostOfTravel',
                    'futureCostOfCare',
                    'customHeadsOfLoss'
                ];

                let all = [
                    'generalDamages',
                    'bereavementAward',
                    'funeralExpenses',
                    'lossOfSpouse',
                    'pastLossOfEarnings',
                    'pastCostOfTravel',
                    'miscellaneous',
                    'pastCostOfCare',
                    'futureCostOfCare',
                    'customHeadsOfLoss',
                    'pastDependencyOnIncome',
                    'futureDependencyOnIncome',
                    'pastDependencyOnServices',
                    'futureDependencyOnServices',
                    'futureLossTotal',
                    'futureCostOfServices',
                    'futurePurchases',
                    'futureLossOfPension'
                ];

                let personalInjury = [
                    'generalDamages',
                    'pastLossOfEarnings',
                    'pastCostOfTravel',
                    'miscellaneous',
                    'pastCostOfCare',
                    'customHeadsOfLoss',
                    'futureCostOfServices',
                    'futureCostOfCare',
                    'futureLossOfEarnings',
                    'futurePurchases',
                    'futureLossOfPension'
                ];

                switch (this.claimType) {
                    case 'fatal_accident_dependency' :
                        return fatalAccidentDependency;
                    case 'fatal_accident_estate' :
                        return fatalAccidentEstate;
                    case 'lost_years' :
                        return lostYears;
                    case 'personal_injury' :
                        return personalInjury;
                    default :
                        return all;
                }
            },
            numberOfRates: {
                get() {
                    return this.chosenRates.length;
                },
            },
            gapClass: {
                get() {
                    return this.chosenRates.length >= 2 
                        ? 'flex-grid__g-1'
                        : 'flex-grid__g-3';
                },
            },
            labels: {
                get() {
                    let graphData = this.graphData(this.chosenRates[0])

                    let data = _.reduce(this.headOfLossOrder, (data, name) => {
                        data.push(name);
                        return data;
                    }, []);

                    return _.map(data, (value) => _.startCase(value).replace('Diy', 'DIY'));
                },
            },
        },
        methods: {
            updateTable(type) {
                this.tableType = type;
            },
            graphData(rate) {
                if (this.tableType === 'total') {
                    return this.totalHeadsOfLossGraph(rate);
                }

                return this.averageHeadsOfLossGraph(rate);
            },
            tableData() {
                if (this.tableType === 'total') {
                    return this.totalHeadsOfLossTable;
                }

                return this.averageHeadsOfLossTable;
            },
            table() {
                return _.reduce(this.headOfLossOrder, (table, name) => {
                    table[name] = this.tableData()[name];
                    return table;
                }, {});
            },
            dataset(rate) {
                let graphData = this.graphData(rate);

                let data = _.reduce(this.headOfLossOrder, (data, name) => {
                    data.push(graphData[name]);
                    return data;
                }, []);

                return [{
                    data,
                    backgroundColor: this.colours,
                    borderWidth: 0,
                }];
            },
            sumOf(rate) {
                let data = this.graphData(rate);
                return Object.keys(data)
                    .reduce((total, key) => {
                        return total + parseFloat(data[key]);
                    }, 0);
            },
            // Needs to be a mixin
            roundAndPercent(val1, val2) {
                return this.roundValue(this.percentageDifference(val1, val2), 2);
            },
            chartLegend()
            {
                let fatalAccidentDependency = [
                    'General Damages',
                    'Bereavement Award',
                    'Funeral Expenses',
                    'Loss Of Spouse',
                    'Past Loss Of Earnings',
                    'Past Cost Of Travel',
                    'Misc',
                    'Past Cost Of Care',
                    'Custom Heads Of Loss',
                    'Past Dependency On Income',
                    'Future Dependency On Income',
                    'Past Dependency On Services',
                    'Future Dependency On Services'
                ];

                let fatalAccidentEstate = [
                    'General Damages',
                    'Funeral Expenses',
                    'Loss Of Spouse',
                    'Past Loss Of Earnings',
                    'Past Cost Of Travel',
                    'Misc',
                    'Past Cost Of Care',
                    'Custom Heads Of Loss',
                ];

                let lostYears = [
                    'General Damages',
                    'Future Loss Total',
                    'Misc',
                    'Past Cost Of Care',
                    'Past Loss Of Earnings',
                    'Past Cost Of Travel',
                    'Future Cost Of Care',
                    'Custom Heads Of Loss'
                ];

                let all = [
                    'General Damages',
                    'Bereavement Award',
                    'Funeral Expenses',
                    'Loss Of Spouse',
                    'Past Loss Of Earnings',
                    'Past Cost Of Travel',
                    'Misc',
                    'Past Cost Of Care',
                    'Future Cost Of Care',
                    'Custom Heads Of Loss',
                    'Past Dependency On Income',
                    'Future Dependency On Income',
                    'Past Dependency On Services',
                    'Future Dependency On Services',
                    'Future Loss Total'
                ];

                let personalInjury = [
                    'General Damages',
                    'Past Loss Of Earnings',
                    'Past Cost Of Travel',
                    'Past Cost Of Care',
                    'Miscellaneous',
                    'Custom Heads Of Loss',
                    'Future Cost of Services',
                    'Future Cost of Care',
                    'Future Loss of Income',
                    'Future Cost of Purchases',
                    'Future Loss of Pension'
                ];

                switch (this.claimType) {
                    case 'fatal_accident_dependency' :
                        return fatalAccidentDependency;
                    case 'fatal_accident_estate' :
                        return fatalAccidentEstate;
                    case 'lost_years' :
                        return lostYears;
                    case 'personal_injury' :
                        return personalInjury;
                    default :
                        return all;
                }
            }
        }
    }
</script>



// WEBPACK FOOTER //
// AllHeadsOfLoss.vue?64f180dd